import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

// Iter83 — Custom dropdown rewritten so it:
//   1. Respects `className` on DropdownMenuContent (was hardcoded `w-48`).
//   2. Supports the `asChild` prop on DropdownMenuTrigger so callers can
//      wrap their own Button without ending up with a <button> nested
//      inside a <button> (which the previous version did, causing the
//      auto-added ChevronDown to appear next to existing icons).
//   3. Reads `sideOffset` for spacing, defaults `align` to "end" which is
//      what every existing call site already passes.
//   4. Reliably stays on screen via `right-0` + min width so it can't be
//      clipped at the right edge of the chat panel (HR Inbox menu issue
//      reported by Ankur 03-Jun-2026).
export function DropdownMenu({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {React.Children.map(children, (child) => {
        if (!child) return null;
        if (child.type === DropdownMenuTrigger) {
          return React.cloneElement(child, { onClick: () => setIsOpen(!isOpen), isOpen });
        }
        if (child.type === DropdownMenuContent) {
          return isOpen ? React.cloneElement(child, { onClose: () => setIsOpen(false) }) : null;
        }
        return child;
      })}
    </div>
  );
}

export function DropdownMenuTrigger({ children, onClick, isOpen, className = '', asChild = false }) {
  if (asChild && React.isValidElement(children)) {
    // Wrap the user-supplied element with the click handler instead of
    // nesting another <button>. This is the shadcn/Radix convention.
    return React.cloneElement(children, {
      onClick: (e) => {
        if (children.props.onClick) children.props.onClick(e);
        onClick && onClick(e);
      },
      'aria-expanded': isOpen,
    });
  }
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center justify-center ${className}`}
    >
      {children}
      <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
}

export function DropdownMenuContent({
  children,
  onClose,
  align = 'end',
  sideOffset = 4,
  className = '',
}) {
  // `align="end"` and `align="right"` both anchor to the right edge of the
  // trigger. `align="start"`/`"left"` anchor to the left. Any explicit
  // width / colour in the caller's `className` wins over these defaults
  // (e.g. min-w-[220px], bg-[#1a2942]).
  const alignClass = align === 'start' || align === 'left' ? 'left-0' : 'right-0';
  return (
    <div
      className={`absolute ${alignClass} rounded-md shadow-lg bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 z-50 animate-fade-in min-w-[12rem] ${className}`}
      style={{ marginTop: `${sideOffset}px` }}
    >
      <div className="py-1" onClick={onClose}>
        {children}
      </div>
    </div>
  );
}

export function DropdownMenuItem({ children, onClick, icon: Icon, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2 transition-colors whitespace-nowrap ${className}`}
    >
      {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
      <span>{children}</span>
    </button>
  );
}
